import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, FileDown, ClipboardList, BookOpen, Printer, ExternalLink } from 'lucide-react';
import {
    downloadMgFormblattPdf,
    downloadMgVorabFragebogenPdf,
    generateMgFormblattPdf,
    generateMgVorabFragebogenPdf,
    getMgKpiErreichtQuote,
    getMgKpiLevel,
    MG_LEITFADEN,
    MG_VORAB_FRAGEN,
    type MgKennzahlen,
    type MgKpiLevelKey,
} from '../../utils/mitarbeiterGespraech/mgFormblattPdf';

/** Zonen-Farben (Dynamisierung) als Tailwind-Klassen — identische Logik wie im Performance-Modal */
const LEVEL_TW: Record<MgKpiLevelKey, { text: string; bar: string }> = {
    exzellent: { text: 'text-purple-400', bar: 'bg-purple-500' },
    sehrGut: { text: 'text-emerald-400', bar: 'bg-emerald-500' },
    gut: { text: 'text-lime-400', bar: 'bg-lime-500' },
    grenzwertig: { text: 'text-amber-400', bar: 'bg-amber-400' },
    kritisch: { text: 'text-orange-400', bar: 'bg-orange-500' },
    defizitaer: { text: 'text-red-400', bar: 'bg-red-500' },
};

export interface MitarbeiterGespraechModalProps {
    isOpen: boolean;
    onClose: () => void;
    employeeName: string;
    inhaberName: string;
    zeitraum: string;
    kennzahlen: MgKennzahlen;
    currencyCode?: string;
}

const fmtPct = (ratio: number) =>
    `${(Number(ratio || 0) * 100).toLocaleString('de-DE', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    })} %`;

const fmtNum = (n: number, d = 2) =>
    Number(n || 0).toLocaleString('de-DE', {
        minimumFractionDigits: d,
        maximumFractionDigits: d,
    });

const fmtEur = (n: number, currency = 'EUR') =>
    `${fmtNum(n, 0)} ${currency}`;

/** Differenz in Prozentpunkten mit Vorzeichen, z.B. "-5,8 Pkt." */
const fmtPkt = (ratio: number) =>
    `${(Number(ratio || 0) * 100) >= 0 ? '+' : ''}${(Number(ratio || 0) * 100).toLocaleString('de-DE', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    })} Pkt.`;

const fmtSigned = (n: number, d = 2) =>
    `${n >= 0 ? '+' : ''}${fmtNum(n, d)}`;

/** ISO-Datum (yyyy-mm-dd) → deutsches Anzeigeformat */
const fmtDateDe = (iso: string) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    if (!y || !m || !d) return iso;
    return `${d}.${m}.${y}`;
};

/**
 * Sub-Modal: Formblatt MG-01/02 vorbereiten.
 * Ein durchgängiger Scroll: Anleitung → Kennzahlen → PDF zum Lesen → Drucken.
 * Leitfaden erscheint nur hier, nicht im unterschriebenen PDF.
 * Store-Isolation: nur lokaler State.
 */
const MitarbeiterGespraechModal: React.FC<MitarbeiterGespraechModalProps> = ({
    isOpen,
    onClose,
    employeeName,
    inhaberName,
    zeitraum,
    kennzahlen,
    currencyCode = 'EUR',
}) => {
    const [mounted, setMounted] = useState(false);
    const [durchschnittsBon, setDurchschnittsBon] = useState('');
    const [kundenquote, setKundenquote] = useState('');
    const [massnahme1, setMassnahme1] = useState('');
    const [massnahme2, setMassnahme2] = useState('');
    const [folgeTerminIso, setFolgeTerminIso] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const printFrameRef = useRef<HTMLIFrameElement | null>(null);

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        setDurchschnittsBon('');
        setKundenquote('');
        setMassnahme1('');
        setMassnahme2('');
        setFolgeTerminIso('');
        setErr(null);
        setBusy(false);
    }, [isOpen, employeeName]);

    if (!isOpen || !mounted) return null;

    const folgeTerminDe = fmtDateDe(folgeTerminIso);

    const kpis: Array<{
        label: string;
        type: 'faktor' | 'vk' | 'auslastung';
        ziel: string;
        ist: string;
        istNum: number;
        zielNum: number;
        diffText: string;
    }> = [
        {
            label: 'Multiplikator (DL)',
            type: 'faktor',
            ziel: `${fmtNum(kennzahlen.multiplikator, 2)} x`,
            ist: `${fmtNum(kennzahlen.istMultiplikator, 2)} x`,
            istNum: kennzahlen.istMultiplikator,
            zielNum: kennzahlen.multiplikator,
            diffText: `${fmtSigned(kennzahlen.istMultiplikator - kennzahlen.multiplikator, 2)} x`,
        },
        {
            label: 'VK-Quote',
            type: 'vk',
            ziel: fmtPct(kennzahlen.vkZiel),
            ist: fmtPct(kennzahlen.vkQuote),
            istNum: kennzahlen.vkQuote,
            zielNum: kennzahlen.vkZiel,
            diffText: fmtPkt(kennzahlen.vkQuote - kennzahlen.vkZiel),
        },
        {
            label: 'Umsatzauslastung',
            type: 'auslastung',
            ziel: fmtPct(kennzahlen.auslastungZiel),
            ist: fmtPct(kennzahlen.umsatzauslastung),
            istNum: kennzahlen.umsatzauslastung,
            zielNum: kennzahlen.auslastungZiel,
            diffText: fmtPkt(kennzahlen.umsatzauslastung - kennzahlen.auslastungZiel),
        },
        {
            label: 'Stundenumsatz (Brutto)',
            type: 'faktor',
            ziel: fmtEur(kennzahlen.basisStdUmsatz, currencyCode),
            ist: fmtEur(kennzahlen.istStdUmsatz, currencyCode),
            istNum: kennzahlen.istStdUmsatz,
            zielNum: kennzahlen.basisStdUmsatz,
            diffText: `${fmtSigned(kennzahlen.istStdUmsatz - kennzahlen.basisStdUmsatz, 2)} ${currencyCode}`,
        },
        {
            label: 'Monatsumsatz (Netto, Ø)',
            type: 'faktor',
            ziel: fmtEur(kennzahlen.umsatzZielNetto, currencyCode),
            ist: fmtEur(kennzahlen.umsatzIstNetto, currencyCode),
            istNum: kennzahlen.umsatzIstNetto,
            zielNum: kennzahlen.umsatzZielNetto,
            diffText: `${fmtSigned(kennzahlen.umsatzIstNetto - kennzahlen.umsatzZielNetto, 0)} ${currencyCode}`,
        },
    ];

    const buildParams = () => ({
        employeeName,
        inhaberName,
        zeitraum,
        kennzahlen,
        durchschnittsBon,
        kundenquote,
        massnahme1,
        massnahme2,
        folgeTermin: folgeTerminDe,
        currencyCode,
    });

    const handleDownload = () => {
        setBusy(true);
        setErr(null);
        try {
            downloadMgFormblattPdf(buildParams());
        } catch (e: any) {
            console.error('[MG-01/02 PDF]', e);
            setErr(e?.message || 'PDF-Generierung fehlgeschlagen.');
        } finally {
            setBusy(false);
        }
    };

    const buildVorabParams = () => ({
        employeeName,
        inhaberName,
    });

    const handleVorabDownload = () => {
        setBusy(true);
        setErr(null);
        try {
            downloadMgVorabFragebogenPdf(buildVorabParams());
        } catch (e: any) {
            console.error('[MG-00 Vorab PDF]', e);
            setErr(e?.message || 'Vorab-Fragebogen konnte nicht erstellt werden.');
        } finally {
            setBusy(false);
        }
    };

    const handleVorabPrint = () => {
        setErr(null);
        try {
            const { blob } = generateMgVorabFragebogenPdf(buildVorabParams());
            const url = URL.createObjectURL(blob);
            const frame = printFrameRef.current;
            if (!frame) {
                URL.revokeObjectURL(url);
                setErr('Druckrahmen nicht bereit. Bitte Vorabblatt speichern und dort drucken.');
                return;
            }
            const onLoad = () => {
                try {
                    frame.contentWindow?.focus();
                    frame.contentWindow?.print();
                } catch (e) {
                    console.error('[MG-00 Vorab Druck]', e);
                    setErr('Drucken fehlgeschlagen. Bitte Vorabblatt speichern und dort drucken.');
                } finally {
                    setTimeout(() => URL.revokeObjectURL(url), 60_000);
                    frame.removeEventListener('load', onLoad);
                }
            };
            frame.addEventListener('load', onLoad);
            frame.src = url;
        } catch (e) {
            console.error('[MG-00 Vorab Druck]', e);
            setErr('Drucken fehlgeschlagen. Bitte Vorabblatt speichern und dort drucken.');
        }
    };

    /** Volles Protokoll-PDF (mit Dynamisierung) nur zum Lesen — kein Druckdialog */
    const handleAnsehen = () => {
        setErr(null);
        try {
            const { blob } = generateMgFormblattPdf(buildParams());
            const url = URL.createObjectURL(blob);
            const w = window.open(url, '_blank', 'noopener,noreferrer');
            if (!w) {
                URL.revokeObjectURL(url);
                setErr('Popup blockiert. Bitte Popups erlauben oder „PDF speichern“ nutzen.');
                return;
            }
            setTimeout(() => URL.revokeObjectURL(url), 120_000);
        } catch (e: any) {
            console.error('[MG-01/02 Ansehen]', e);
            setErr(e?.message || 'Vorschau fehlgeschlagen.');
        }
    };

    /** Druckdialog erst hier — nicht beim Ansehen */
    const handlePrint = () => {
        setErr(null);
        try {
            const { blob } = generateMgFormblattPdf(buildParams());
            const url = URL.createObjectURL(blob);
            const frame = printFrameRef.current;
            if (!frame) {
                URL.revokeObjectURL(url);
                setErr('Druckrahmen nicht bereit. Bitte PDF speichern und dort drucken.');
                return;
            }
            const onLoad = () => {
                try {
                    frame.contentWindow?.focus();
                    frame.contentWindow?.print();
                } catch (e) {
                    console.error('[MG-01/02 Druck]', e);
                    setErr('Drucken fehlgeschlagen. Bitte PDF speichern und dort drucken.');
                } finally {
                    setTimeout(() => URL.revokeObjectURL(url), 60_000);
                    frame.removeEventListener('load', onLoad);
                }
            };
            frame.addEventListener('load', onLoad);
            frame.src = url;
        } catch (e) {
            console.error('[MG-01/02 Druck]', e);
            setErr('Drucken fehlgeschlagen. Bitte PDF speichern und dort drucken.');
        }
    };

    const openerText = MG_LEITFADEN.opener(employeeName.split(/\s+/)[0] || employeeName);

    return createPortal(
        <div
            className="fixed inset-0 z-[2147483647] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            <div
                className="relative w-full max-w-6xl max-h-[94vh] rounded-2xl border border-[#2a2a2a] bg-[#0f1015] shadow-2xl text-slate-200 flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Versteckter Rahmen nur für den Druckdialog — nicht sichtbar */}
                <iframe
                    ref={printFrameRef}
                    title="MG Druckrahmen"
                    className="absolute w-0 h-0 opacity-0 pointer-events-none border-0"
                    aria-hidden
                />

                {/* Header */}
                <div className="flex-shrink-0 flex items-start justify-between gap-3 border-b border-[#222] bg-[#08090b] px-6 py-4">
                    <div className="flex items-start gap-3 min-w-0">
                        <div className="mt-0.5 p-2 rounded-lg border border-[#E6C299]/30 bg-[#E6C299]/10 text-[#E6C299]">
                            <ClipboardList className="w-6 h-6" />
                        </div>
                        <div className="min-w-0">
                            <h3 className="text-lg font-black uppercase tracking-wider text-white truncate">
                                Entwicklungsgespräch
                            </h3>
                            <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold mt-0.5">
                                Formblatt MG-01/02 · {employeeName}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 rounded-full border border-[#333] text-slate-400 hover:text-white hover:bg-[#1a1a1a] transition-colors"
                        aria-label="Schließen"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Ein einziger Scroll: Anleitung → Daten → Lesen → Drucken */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-8">
                    {/* —— LEITFADEN (lesefreundlich, dokumentartig) —— */}
                    <section>
                        <div className="rounded-2xl border border-slate-300 bg-[#f7f6f2] text-slate-800 shadow-inner overflow-hidden">
                            <div className="px-5 py-4 border-b border-slate-200 bg-white/70 flex items-center gap-2">
                                <BookOpen className="w-5 h-5 text-slate-600" />
                                <div>
                                    <h4 className="text-sm font-black uppercase tracking-widest text-slate-800">
                                        Gespräch vorbereiten — Anleitung
                                    </h4>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                        Nur für dich als Gesprächsführer. Erscheint nicht im unterschriebenen PDF.
                                    </p>
                                </div>
                            </div>

                            <div className="p-5 md:p-6 space-y-7">
                                <div>
                                    <h5 className="text-xs font-black uppercase tracking-widest text-slate-500 mb-2">
                                        1 · Gesprächsöffner (erste 3 Minuten)
                                    </h5>
                                    <div className="rounded-xl border border-slate-200 bg-white px-4 py-4 text-[15px] leading-7 text-slate-700 whitespace-pre-line">
                                        {openerText}
                                    </div>
                                </div>

                                <div>
                                    <h5 className="text-xs font-black uppercase tracking-widest text-slate-500 mb-3">
                                        2 · Strukturierter Ablauf (ca. 45–60 Min.)
                                    </h5>
                                    <ol className="space-y-3">
                                        {MG_LEITFADEN.steps.map((s, i) => (
                                            <li
                                                key={s.title}
                                                className="flex gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3"
                                            >
                                                <span className="shrink-0 w-7 h-7 rounded-full bg-slate-800 text-white text-xs font-black flex items-center justify-center mt-0.5">
                                                    {i + 1}
                                                </span>
                                                <div className="min-w-0">
                                                    <div className="text-sm font-bold text-slate-900 mb-1">{s.title.replace(/^Schritt \d+:\s*/, '')}</div>
                                                    <div className="text-sm text-slate-600 leading-relaxed">{s.body}</div>
                                                </div>
                                            </li>
                                        ))}
                                    </ol>
                                </div>

                                <div>
                                    <h5 className="text-xs font-black uppercase tracking-widest text-slate-500 mb-3">
                                        3 · Checkliste vor dem Gespräch
                                    </h5>
                                    <ul className="space-y-2">
                                        {MG_LEITFADEN.checks.map((c) => (
                                            <li
                                                key={c.title}
                                                className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3"
                                            >
                                                <span className="mt-1 w-4 h-4 rounded border-2 border-slate-400 shrink-0" />
                                                <div>
                                                    <div className="text-sm font-bold text-slate-900">{c.title}</div>
                                                    <div className="text-sm text-slate-600 leading-relaxed">{c.body}</div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="rounded-xl border border-[#E6C299]/40 bg-[#fff8ef] px-4 py-4 space-y-3">
                                    <div>
                                        <h5 className="text-xs font-black uppercase tracking-widest text-[#92683a] mb-1">
                                            Vorabblatt für den Mitarbeiter (MG-00)
                                        </h5>
                                        <p className="text-sm text-slate-600 leading-relaxed">
                                            Druckbares Blatt mit {MG_VORAB_FRAGEN.length} Fragen zur Selbsteinschätzung —
                                            ca. 7 Tage vorher aushändigen. Der Mitarbeiter bringt es ausgefüllt mit.
                                        </p>
                                    </div>
                                    <ol className="space-y-1.5 pl-1">
                                        {MG_VORAB_FRAGEN.map((q) => (
                                            <li key={q.nr} className="text-sm text-slate-700 leading-snug flex gap-2">
                                                <span className="shrink-0 font-black text-[#92683a]">{q.nr}.</span>
                                                <span>{q.title}</span>
                                            </li>
                                        ))}
                                    </ol>
                                    <div className="flex flex-col sm:flex-row gap-2 pt-1">
                                        <button
                                            type="button"
                                            onClick={handleVorabPrint}
                                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-[#92683a]/40 bg-white text-[#92683a] text-xs font-black uppercase tracking-widest hover:bg-[#fff3e0] transition-colors"
                                        >
                                            <Printer className="w-4 h-4" />
                                            Vorabblatt drucken
                                        </button>
                                        <button
                                            type="button"
                                            disabled={busy}
                                            onClick={handleVorabDownload}
                                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-[#92683a]/40 bg-[#92683a] text-white text-xs font-black uppercase tracking-widest hover:bg-[#7a5630] disabled:opacity-50 transition-colors"
                                        >
                                            <FileDown className="w-4 h-4" />
                                            {busy ? 'Generiere…' : 'Vorabblatt speichern'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    <div className="border-t border-[#222]" />

                    {/* —— PROTOKOLL-DATEN —— */}
                    <section className="space-y-5">
                        <div className="flex items-center gap-2">
                            <ClipboardList className="w-5 h-5 text-[#E6C299]" />
                            <h4 className="text-sm font-black uppercase tracking-widest text-[#E6C299]">
                                Ergebnis-Protokoll — Kennzahlen &amp; Maßnahmen
                            </h4>
                        </div>

                        <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="rounded-lg border border-[#222] bg-black/40 px-3 py-2.5">
                                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Gesprächsführer</div>
                                <div className="text-slate-200 font-semibold truncate">{inhaberName || '—'}</div>
                            </div>
                            <div className="rounded-lg border border-[#222] bg-black/40 px-3 py-2.5">
                                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Zeitraum</div>
                                <div className="text-slate-200 font-semibold truncate">{zeitraum || '—'}</div>
                            </div>
                        </div>

                        <div>
                            <h5 className="text-[11px] font-black uppercase tracking-widest text-[#E6C299] mb-2">
                                PEOne-Kennzahlen — Ziel / Ist / Differenz
                            </h5>
                            <div className="rounded-xl border border-[#2a2a2a] bg-[#15171e] overflow-hidden">
                                <div className="grid grid-cols-[1.15fr_0.7fr_0.7fr_0.9fr_1.2fr] gap-2 px-3 py-2 border-b border-[#222] bg-black/40">
                                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Kennzahl</span>
                                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold text-right">Ziel</span>
                                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold text-right">Ist</span>
                                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold text-right">Differenz</span>
                                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold text-right">Zielerreichung</span>
                                </div>
                                <div className="divide-y divide-[#222]">
                                    {kpis.map((row) => {
                                        const level = getMgKpiLevel(row.type, row.istNum, row.zielNum);
                                        const quote = getMgKpiErreichtQuote(row.istNum, row.zielNum);
                                        const tw = LEVEL_TW[level.key];
                                        return (
                                            <div key={row.label} className="grid grid-cols-[1.15fr_0.7fr_0.7fr_0.9fr_1.2fr] gap-2 px-3 py-3 items-center">
                                                <span className="text-sm text-slate-300">{row.label}</span>
                                                <span className="text-sm font-mono text-slate-400 text-right">{row.ziel}</span>
                                                <span className="text-base font-mono font-bold text-white text-right">{row.ist}</span>
                                                <span className={`text-sm font-mono font-bold text-right ${tw.text}`}>
                                                    {row.diffText}
                                                </span>
                                                <div className="flex flex-col items-end gap-1">
                                                    <span className={`text-[11px] font-black uppercase tracking-wide ${tw.text}`}>
                                                        {Math.round(quote * 100)} % · {level.label}
                                                    </span>
                                                    <div className="w-full h-1.5 rounded-full bg-black/60 border border-[#333] overflow-hidden">
                                                        <div
                                                            className={`h-full rounded-full ${tw.bar}`}
                                                            style={{ width: `${Math.min(100, Math.max(0, quote * 100))}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="flex items-center justify-between gap-3 px-3 py-2.5 border-t border-[#222] bg-black/30">
                                    <span className="text-xs text-slate-500">Referenz: Stundenumsatz-Soll bei 75 % Auslastung</span>
                                    <span className="text-sm font-mono font-bold text-slate-300">{fmtEur(kennzahlen.stdUmsatz75, currencyCode)}</span>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div className="space-y-3">
                                <h5 className="text-[11px] font-black uppercase tracking-widest text-[#E6C299] min-h-[1.25rem]">
                                    Kassendaten (manuell)
                                </h5>
                                <label className="block">
                                    <span className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-1 block">
                                        Durchschnittsbon (Ø-Bon)
                                    </span>
                                    <input
                                        type="text"
                                        value={durchschnittsBon}
                                        onChange={(e) => setDurchschnittsBon(e.target.value)}
                                        placeholder="z. B. 62 € / steigend"
                                        className="w-full h-11 rounded-lg border border-[#333] bg-black px-3 text-sm text-white placeholder:text-slate-600 focus:border-[#E6C299]/60 outline-none"
                                    />
                                </label>
                                <label className="block">
                                    <span className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-1 block">
                                        Kundenquote / Frequenz
                                    </span>
                                    <input
                                        type="text"
                                        value={kundenquote}
                                        onChange={(e) => setKundenquote(e.target.value)}
                                        placeholder="z. B. 75% Stamm / 25% Neu"
                                        className="w-full h-11 rounded-lg border border-[#333] bg-black px-3 text-sm text-white placeholder:text-slate-600 focus:border-[#E6C299]/60 outline-none"
                                    />
                                </label>
                            </div>

                            <div className="space-y-3">
                                <h5 className="text-[11px] font-black uppercase tracking-widest text-[#E6C299] min-h-[1.25rem]">
                                    Zielvereinbarung (optional)
                                </h5>
                                <label className="block">
                                    <span className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-1 block">
                                        Maßnahme 1
                                    </span>
                                    <input
                                        type="text"
                                        value={massnahme1}
                                        onChange={(e) => setMassnahme1(e.target.value)}
                                        placeholder="z. B. VK-Quote auf mind. 5 % …"
                                        className="w-full h-11 rounded-lg border border-[#333] bg-black px-3 text-sm text-white placeholder:text-slate-600 focus:border-[#E6C299]/60 outline-none"
                                    />
                                </label>
                                <label className="block">
                                    <span className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-1 block">
                                        Maßnahme 2
                                    </span>
                                    <input
                                        type="text"
                                        value={massnahme2}
                                        onChange={(e) => setMassnahme2(e.target.value)}
                                        placeholder="z. B. 75 %-Stundenumsatz durch Veredelungen …"
                                        className="w-full h-11 rounded-lg border border-[#333] bg-black px-3 text-sm text-white placeholder:text-slate-600 focus:border-[#E6C299]/60 outline-none"
                                    />
                                </label>
                            </div>
                        </div>

                        <label className="block max-w-md">
                            <span className="text-[11px] font-black uppercase tracking-widest text-[#E6C299] mb-2 block">
                                Nächstes Gespräch / Zwischenkontrolle
                            </span>
                            <input
                                type="date"
                                value={folgeTerminIso}
                                onChange={(e) => setFolgeTerminIso(e.target.value)}
                                className="w-full h-11 rounded-lg border border-[#333] bg-black px-3 text-sm text-white focus:border-[#E6C299]/60 outline-none [color-scheme:dark]"
                            />
                            <span className="mt-1.5 block text-xs text-slate-500">
                                Erscheint im Protokoll{folgeTerminDe ? `: ${folgeTerminDe}` : ' (noch offen)'}.
                            </span>
                        </label>
                    </section>

                    <div className="border-t border-[#222]" />

                    {/* —— ANSEHEN / DRUCKEN —— */}
                    <section className="space-y-3 pb-2">
                        <h4 className="text-sm font-black uppercase tracking-widest text-[#E6C299]">
                            Protokoll ansehen / drucken
                        </h4>
                        <p className="text-sm text-slate-400 leading-relaxed">
                            <strong className="text-slate-200">Ansehen</strong> öffnet das vollständige Protokoll-PDF
                            (Kennzahlen mit Dynamisierung, Maßnahmen, Folgetermin, Unterschriften) — nur zum Lesen,
                            ohne Druckdialog. <strong className="text-slate-200">Drucken</strong> öffnet erst den Druckdialog.
                        </p>
                        {err && <p className="text-sm font-bold text-red-400">{err}</p>}
                        <div className="flex flex-col sm:flex-row gap-2">
                            <button
                                type="button"
                                onClick={handleAnsehen}
                                className="flex-[1.3] flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-[#E6C299]/50 bg-[#E6C299]/15 text-[#E6C299] text-xs font-black uppercase tracking-widest hover:bg-[#E6C299]/25 transition-colors"
                            >
                                <ExternalLink className="w-4 h-4" />
                                Protokoll ansehen
                            </button>
                            <button
                                type="button"
                                onClick={handlePrint}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-sky-500/40 bg-sky-500/10 text-sky-300 text-xs font-black uppercase tracking-widest hover:bg-sky-500/20 transition-colors"
                            >
                                <Printer className="w-4 h-4" />
                                Jetzt drucken
                            </button>
                            <button
                                type="button"
                                disabled={busy}
                                onClick={handleDownload}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-[#333] text-slate-300 text-xs font-black uppercase tracking-widest hover:bg-[#15171e] disabled:opacity-50 transition-colors"
                            >
                                <FileDown className="w-4 h-4" />
                                {busy ? 'Generiere…' : 'PDF speichern'}
                            </button>
                        </div>
                    </section>
                </div>

                {/* Footer */}
                <div className="flex-shrink-0 border-t border-[#222] bg-[#08090b] px-6 py-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-full px-4 py-2.5 rounded-xl border border-[#333] text-slate-300 text-xs font-black uppercase tracking-widest hover:bg-[#15171e] transition-colors"
                    >
                        Schließen
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default MitarbeiterGespraechModal;
